import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Offcanvas,
    Table,
    Pagination,
    Form,
    Button,
    Row,
    Col,
    InputGroup,
    Spinner,
} from 'react-bootstrap';
import { IconSearch } from '@tabler/icons-react';
import { DateRange, DateRangePicker, RangeKeyDict, Range } from 'react-date-range';
import { format } from 'date-fns';
import { useMediaQuery } from '../hooks/useMediaQuery';

// Import hook, tipe, dan skema yang diperlukan
import useDataCalendarV3 from '../../../services/booking-v3/hooks/useDataCalendarV3';
import { BookingType } from '../../../services/booking-v3/response/DataCalendarResponseV3';

// Import CSS
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useAuth } from '../../../auth/context/AuthProvider';

interface AllUnassignedRoomsProps {
    show: boolean;
    handleClose: () => void;
    onJumpToBooking: (roomTypeId: string, checkinDate: string, checkoutDate: string) => void;
}

const ITEMS_PER_PAGE = 10;
const initialFilterState = { keyword: '', startDate: '', endDate: '' };

const AllUnassignedRooms: React.FC<AllUnassignedRoomsProps> = ({ show, handleClose, onJumpToBooking }) => {
    const { user } = useAuth();
    const isMobile = useMediaQuery('(max-width: 768px)');

    // State untuk filter
    const [tempFilters, setTempFilters] = useState(initialFilterState);
    const [appliedFilters, setAppliedFilters] = useState(initialFilterState);

    const [currentPage, setCurrentPage] = useState(1);

    // State dan Ref untuk Date Range Picker
    const [dateRange, setDateRange] = useState<Range[]>([
        { startDate: new Date(), endDate: new Date(), key: 'selection' }
    ]);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const calendarRef = useRef<HTMLDivElement>(null);

    // Mempersiapkan parameter untuk hook
    const apiParams = useMemo(() => {
        const filter: any = {
            room_id: "unassigned",
            property_id: user?.active_property_id, // Ambil dari user aktif
        };

        if (appliedFilters.startDate && appliedFilters.endDate) {
            filter.date = {
                gte: appliedFilters.startDate,
                lte: appliedFilters.endDate,
            };
        }

        return { filter };
    }, [appliedFilters, user]); // Tambahkan `user` ke dependency array

    // Memanggil hook untuk mengambil data dari API
    const { data: apiResponse, isLoading, isError, error } = useDataCalendarV3(apiParams, {
        queryKey: ['unassignedRooms', apiParams],
        // API hanya akan dipanggil jika Offcanvas terbuka DAN property_id tersedia
        enabled: show && !!user?.active_property_id,
    });

    // Data dari API difilter secara lokal berdasarkan keyword
    const locallyFilteredData = useMemo(() => {
        // Jika tidak ada keyword, kembalikan semua data
        if (!appliedFilters.keyword) {
            return apiResponse?.data || [];
        }

        const lowercasedKeyword = appliedFilters.keyword.toLowerCase();

        return (apiResponse?.data || []).filter(
            (room: BookingType) =>
                room.customer_name.toLowerCase().includes(lowercasedKeyword) ||
                (room.customer_surname ?? '').toLowerCase().includes(lowercasedKeyword)
        );
    }, [apiResponse, appliedFilters.keyword]);


    useEffect(() => { setCurrentPage(1); }, [locallyFilteredData]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                setIsCalendarOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [calendarRef]);

    // Kalkulasi untuk paginasi lokal
    const lastItemIndex = currentPage * ITEMS_PER_PAGE;
    const firstItemIndex = lastItemIndex - ITEMS_PER_PAGE;
    const currentItems = locallyFilteredData.slice(firstItemIndex, lastItemIndex);
    const totalPages = Math.ceil(locallyFilteredData.length / ITEMS_PER_PAGE);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTempFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleDateChange = (rangesByKey: RangeKeyDict) => {
        const { selection } = rangesByKey;
        if (selection.startDate && selection.endDate) {
            setDateRange([selection]);
            setTempFilters(prev => ({
                ...prev,
                startDate: format(selection.startDate!, 'yyyy-MM-dd'),
                endDate: format(selection.endDate!, 'yyyy-MM-dd')
            }));
        }
    };

    // Fungsi apply filter sekarang hanya mengatur state, yang akan memicu useMemo
    const handleApplyFilters = () => {
        setAppliedFilters(tempFilters);
    };

    const resetFilters = () => {
        setTempFilters(initialFilterState);
        setAppliedFilters(initialFilterState);
        setDateRange([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
    };

    // Styling untuk posisi kalender
    const calendarContainerStyle: React.CSSProperties = {
        position: 'absolute', zIndex: 1000, top: '100%', marginTop: '8px',
        border: '1px solid #dfe2e5', borderRadius: '8px', boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
        ...(isMobile ? { left: '50%', transform: 'translateX(-50%)' } : { right: 0 })
    };

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" style={{ width: '100%' }} backdrop="static">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>All Unassigned Rooms</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <div className="mb-4 p-3 border rounded">
                    <h5 className="mb-3">Filters</h5>
                    <Row className="g-3 align-items-end">
                        <Col md={12} lg={4}>
                            <Form.Group>
                                <Form.Label>Search by Guest Name</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text><IconSearch size={18} /></InputGroup.Text>
                                    <Form.Control
                                        type="text" name="keyword"
                                        placeholder="e.g., John Doe"
                                        value={tempFilters.keyword} onChange={handleInputChange}
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        <Col md={12} lg={6}>
                            <Form.Group>
                                <Form.Label>Date Range</Form.Label>
                                <div className="position-relative" ref={calendarRef}>
                                    <div className="input-group">
                                        <input
                                            type='text' readOnly className='form-control'
                                            onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                                            value={tempFilters.startDate && tempFilters.endDate ? `${format(new Date(tempFilters.startDate), 'dd MMM yyyy')} - ${format(new Date(tempFilters.endDate), 'dd MMM yyyy')}` : 'Select date range (optional)'}
                                        />
                                        <span className="input-group-text" onClick={() => setIsCalendarOpen(!isCalendarOpen)} style={{ cursor: 'pointer' }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calendar-event" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path><path d="M16 3l0 4"></path><path d="M8 3l0 4"></path><path d="M4 11l16 0"></path><path d="M8 15h2v2h-2z"></path></svg>
                                        </span>
                                    </div>
                                    {isCalendarOpen && (
                                        <div style={calendarContainerStyle}>
                                            {isMobile ? (<DateRange onChange={handleDateChange} ranges={dateRange} months={1} direction="vertical" />) : (<DateRangePicker onChange={handleDateChange} ranges={dateRange} months={2} direction="horizontal" inputRanges={[]} />)}
                                        </div>
                                    )}
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={12} lg={2}>
                            <div className="d-flex">
                                <Button variant="primary" onClick={handleApplyFilters} className="w-100 me-2" disabled={isLoading}>
                                    {isLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Apply'}
                                </Button>
                                <Button variant="outline-secondary" onClick={resetFilters}>Reset</Button>
                            </div>
                        </Col>
                    </Row>
                </div>

                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>#</th><th>Guest Name</th><th>Source</th>
                            <th>Check-in</th><th>Check-out</th><th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={6} className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-2 mb-0">Fetching data...</p></td></tr>
                        ) : isError ? (
                            <tr><td colSpan={6} className="text-center py-5 text-danger">Failed to load data. <pre>{error.message}</pre></td></tr>
                        ) : currentItems.length > 0 ? (
                            currentItems.map((room, index) => (
                                <tr key={room.id}>
                                    <td>{firstItemIndex + index + 1}</td>
                                    <td>{room.customer_name} {room.customer_surname}</td>
                                    <td>{room.source}</td>
                                    <td>{room.checkin_date}</td>
                                    <td>{room.checkout_date}</td>
                                    <td>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            disabled={!room.room_type_id} // Tombol nonaktif jika tidak ada room_type_id
                                            onClick={() => {
                                                if (room.room_type_id) {
                                                    onJumpToBooking(room.room_type_id, room.checkin_date, room.checkout_date);
                                                    handleClose(); // Langsung tutup Offcanvas setelah diklik
                                                }
                                            }}
                                        >
                                            Jump to Booking
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={6} className="text-center">No unassigned rooms found matching your criteria.</td></tr>
                        )}
                    </tbody>
                </Table>

                {!isLoading && locallyFilteredData.length > ITEMS_PER_PAGE && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination>
                            <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                            {[...Array(totalPages).keys()].map(n => (<Pagination.Item key={n + 1} active={n + 1 === currentPage} onClick={() => setCurrentPage(n + 1)}>{n + 1}</Pagination.Item>))}
                            <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default AllUnassignedRooms;